import {Button, Container, Heading, Section, Separator, Stack, Text} from "@/ui";
/**
 * Error Boundary Component with Customer-Friendly UI
 *
 * Catches React component errors and displays helpful, friendly messages.
 * Technical details are logged internally but NEVER shown to customers.
 *
 * Business Rule: Errors are opportunities to help customers, not blame them.
 *
 * @module shared/components/ErrorBoundary
 */
import type {ErrorInfo, ReactNode} from 'react';
import {Component} from 'react';
import {logger} from '@/shared/lib/logger/logger';
import {COPY} from '@/shared/utils/copy';

interface Props {
  /** Children to render when no error (optional for use as errorElement) */
  children?: ReactNode;
  /** Custom fallback UI to render when error occurs */
  fallback?: ReactNode;
  /** Custom error handler callback */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary Component
 *
 * Catches React errors and displays a customer-friendly error page.
 *
 * What customers see:
 * - Friendly illustration
 * - "Something went wrong" message
 * - Helpful recovery options (Refresh, Go Home, Contact Support)
 *
 * What we log (for debugging):
 * - Full error object
 * - Component stack
 * - User context
 * - Timestamp
 *
 * NEVER shows:
 * - Stack traces
 * - Component names
 * - Technical error messages
 * - Request IDs
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log full error details for debugging (never shown to customer)
    logger.componentError('ErrorBoundary', error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      timestamp: new Date().toISOString(),
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleRefresh = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  private handleContactSupport = (): void => {
    window.location.href = '/contact';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Customer-friendly error UI
      return (
        <Section fullHeight background="transparent" className="bg-gradient-to-b from-background to-muted/30 p-4">
          <Container size="md" className="text-center">
            {/* Friendly Illustration - decorative elements exception */}
            <div className="relative mb-8">
              <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                <span className="text-display-1" role="img" aria-label="Oops">
                  😓
                </span>
              </div>
              {/* Decorative elements */}
              <div className="absolute top-0 right-1/4 w-3 h-3 rounded-full bg-primary/30 motion-safe:animate-pulse" />
              <div className="absolute bottom-4 left-1/4 w-2 h-2 rounded-full bg-accent/40 motion-safe:animate-pulse delay-300" />
            </div>

            {/* Title */}
            <Heading
              level={1}
              size="heading-1"
              weight="semibold"
              align="center"
              mb={3}
            >
              {COPY.errors.generic.split('.')[0]}
            </Heading>

            {/* Message */}
            <Text
              variant="muted"
              size="body-lg"
              align="center"
              className="leading-relaxed mb-8"
            >
              We&apos;re sorry for the inconvenience. Try refreshing the page, or go back to the
              home page.
            </Text>

            {/* Actions */}
            <Stack gap={3} justify="center" className="sm:flex-row">
              <div className="min-w-btn-sm">
                <Button
                  variant="tangi"
                  size="lg"
                  fullWidth
                  onClick={this.handleRefresh}
                >
                  🔄 {COPY.errors.refresh}
                </Button>
              </div>

              <div className="min-w-btn-sm">
                <Button
                  variant="outline"
                  size="lg"
                  fullWidth
                  onClick={this.handleGoHome}
                >
                  🏠 {COPY.errors.goHome}
                </Button>
              </div>
            </Stack>

            {/* Contact Support Link */}
            <Stack gap={2} className="mt-8 pt-6">
              <Separator />
              <Text size="body-sm-responsive" variant="muted" align="center">
                {COPY.help.needHelp}
              </Text>
              <Button
                variant="link"
                size="sm"
                onClick={this.handleContactSupport}
              >
                {COPY.errors.contactUs}
              </Button>
            </Stack>
          </Container>
        </Section>
      );
    }

    return this.props.children;
  }
}
